import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Flag, 
  Plus, 
  Users, 
  Crown, 
  Megaphone, 
  UserPlus, 
  LogOut,
  Settings,
  Trash2,
  User,
  Calendar,
  Shield
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Partiti = () => {
  const { isSindacatoAdmin, user } = useAuth();
  const isAdmin = isSindacatoAdmin();
  
  const [partiti, setPartiti] = useState([]);
  const [mioPartito, setMioPartito] = useState(null);
  const [selectedPartito, setSelectedPartito] = useState(null);
  const [partitoDetail, setPartitoDetail] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [annuncioDialogOpen, setAnnuncioDialogOpen] = useState(false);
  const [ruoloDialogOpen, setRuoloDialogOpen] = useState(false);
  const [presidenteDialogOpen, setPresidenteDialogOpen] = useState(false);
  
  // Forms
  const [partitoForm, setPartitoForm] = useState({ nome: '', descrizione: '', colore: '#3B82F6' });
  const [annuncioForm, setAnnuncioForm] = useState({ titolo: '', contenuto: '' });
  const [ruoloForm, setRuoloForm] = useState({ nome: '', livello: 5 });
  const [selectedPresidente, setSelectedPresidente] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [partitiRes, mioRes] = await Promise.all([
        axios.get(`${API_URL}/partiti`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/partiti/mio`).catch(() => ({ data: null }))
      ]);
      setPartiti(partitiRes.data || []);
      setMioPartito(mioRes.data);
      
      if (isAdmin) {
        const usersRes = await axios.get(`${API_URL}/all-users-complete`).catch(() => ({ data: [] }));
        setAllUsers(usersRes.data || []);
      }
    } catch (error) {
      console.error('Errore nel caricamento partiti:', error);
      setPartiti([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartitoDetail = async (partitoId) => {
    try {
      const res = await axios.get(`${API_URL}/partiti/${partitoId}`);
      setPartitoDetail(res.data);
      setSelectedPartito(partitoId);
    } catch (error) {
      toast.error('Errore nel caricamento dettagli');
    }
  };

  const handleCreatePartito = async () => {
    try {
      await axios.post(`${API_URL}/partiti`, partitoForm);
      toast.success('Partito creato!');
      setCreateDialogOpen(false);
      setPartitoForm({ nome: '', descrizione: '', colore: '#3B82F6' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleIscriviti = async (partitoId) => {
    try {
      await axios.post(`${API_URL}/partiti/${partitoId}/iscrizione`);
      toast.success('Iscrizione completata!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleLascia = async () => {
    if (!mioPartito) return;
    try {
      await axios.delete(`${API_URL}/partiti/${mioPartito.partito.id}/iscrizione`);
      toast.success('Hai lasciato il partito');
      setMioPartito(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleAssegnaPresidente = async () => {
    if (!selectedPartito || !selectedPresidente) return;
    try {
      await axios.patch(`${API_URL}/partiti/${selectedPartito}/presidente?user_id=${selectedPresidente}`);
      toast.success('Presidente assegnato!');
      setPresidenteDialogOpen(false);
      fetchPartitoDetail(selectedPartito);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleCreaAnnuncio = async () => {
    if (!selectedPartito) return;
    try {
      await axios.post(`${API_URL}/partiti/${selectedPartito}/annunci`, annuncioForm);
      toast.success('Annuncio pubblicato!');
      setAnnuncioDialogOpen(false);
      setAnnuncioForm({ titolo: '', contenuto: '' });
      fetchPartitoDetail(selectedPartito);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleCreaRuolo = async () => {
    if (!selectedPartito) return;
    try {
      await axios.post(`${API_URL}/partiti/${selectedPartito}/ruoli`, ruoloForm);
      toast.success('Ruolo creato!');
      setRuoloDialogOpen(false);
      setRuoloForm({ nome: '', livello: 5 });
      fetchPartitoDetail(selectedPartito);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleCambiaRuoloMembro = async (userId, ruoloId) => {
    if (!selectedPartito) return;
    try {
      await axios.patch(`${API_URL}/partiti/${selectedPartito}/membri/${userId}/ruolo?ruolo_id=${ruoloId}`);
      toast.success('Ruolo cambiato!');
      fetchPartitoDetail(selectedPartito);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleEliminaPartito = async (partitoId) => {
    if (!window.confirm('Eliminare questo partito?')) return;
    try {
      await axios.delete(`${API_URL}/partiti/${partitoId}`);
      toast.success('Partito eliminato');
      setSelectedPartito(null);
      setPartitoDetail(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const isPresidente = partitoDetail?.partito?.presidente_id === user?.id;
  const canManagePartito = isAdmin || isPresidente;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="partiti-page" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="page-header flex items-center gap-3">
            <Flag className="w-8 h-8" />
            Partiti Politici
          </h1>
          <p className="text-slate-600 mt-1">
            Unisciti a un partito o creane uno nuovo
          </p>
        </div>
        {isAdmin && (
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" />
                Crea Partito
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nuovo Partito Politico</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Nome Partito *</Label>
                  <Input
                    value={partitoForm.nome}
                    onChange={(e) => setPartitoForm({...partitoForm, nome: e.target.value})}
                    placeholder="Es: Partito Democratico"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descrizione *</Label>
                  <Textarea
                    value={partitoForm.descrizione}
                    onChange={(e) => setPartitoForm({...partitoForm, descrizione: e.target.value})}
                    placeholder="Descrizione del partito..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Colore</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={partitoForm.colore}
                      onChange={(e) => setPartitoForm({...partitoForm, colore: e.target.value})}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={partitoForm.colore}
                      onChange={(e) => setPartitoForm({...partitoForm, colore: e.target.value})}
                      placeholder="#3B82F6"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Annulla</Button>
                  <Button onClick={handleCreatePartito}>Crea Partito</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Il mio partito */}
      {mioPartito && (
        <Card className="border-2" style={{ borderColor: mioPartito.partito?.colore }}>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <Badge style={{ backgroundColor: mioPartito.partito?.colore }} className="text-white mb-2">
                  Il Tuo Partito
                </Badge>
                <CardTitle>{mioPartito.partito?.nome}</CardTitle>
                <CardDescription>Ruolo: {mioPartito.membro?.ruolo_nome}</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => fetchPartitoDetail(mioPartito.partito.id)}>
                Vai al Partito
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Lista Partiti */}
        <div className="lg:col-span-1 space-y-4">
          <h2 className="font-semibold text-lg">Tutti i Partiti ({partiti.length})</h2>
          {partiti.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-8">
                <Flag className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">Nessun partito creato</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {partiti.map((p) => (
                <Card 
                  key={p.id} 
                  className={`cursor-pointer hover:shadow-md transition-shadow ${selectedPartito === p.id ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => fetchPartitoDetail(p.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: p.colore }}
                      >
                        <Flag className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold">{p.nome}</p>
                        <p className="text-xs text-slate-500">{p.membri_count} membri</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Dettaglio Partito */}
        <div className="lg:col-span-2">
          {!partitoDetail ? (
            <Card className="border-dashed h-full">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Flag className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-slate-500">Seleziona un partito per vedere i dettagli</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader style={{ borderBottom: `3px solid ${partitoDetail.partito.colore}` }}>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Flag className="w-5 h-5" style={{ color: partitoDetail.partito.colore }} />
                      {partitoDetail.partito.nome}
                    </CardTitle>
                    <CardDescription className="mt-2">{partitoDetail.partito.descrizione}</CardDescription>
                    {partitoDetail.partito.presidente_nome && (
                      <div className="flex items-center gap-2 mt-2">
                        <Crown className="w-4 h-4 text-amber-500" />
                        <span className="text-sm">Presidente: <strong>@{partitoDetail.partito.presidente_username}</strong></span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {!mioPartito && (
                      <Button onClick={() => handleIscriviti(partitoDetail.partito.id)} className="bg-green-600 hover:bg-green-700">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Iscriviti
                      </Button>
                    )}
                    {mioPartito?.partito?.id === partitoDetail.partito.id && !isPresidente && (
                      <Button variant="outline" onClick={handleLascia} className="text-red-600">
                        <LogOut className="w-4 h-4 mr-2" />
                        Lascia
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="outline" size="icon" className="text-red-600" onClick={() => handleEliminaPartito(partitoDetail.partito.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Tabs defaultValue="membri" className="w-full">
                  <TabsList className="w-full justify-start rounded-none border-b">
                    <TabsTrigger value="membri">Membri ({partitoDetail.membri.length})</TabsTrigger>
                    <TabsTrigger value="annunci">Annunci ({partitoDetail.annunci.length})</TabsTrigger>
                    {canManagePartito && <TabsTrigger value="gestione">Gestione</TabsTrigger>}
                  </TabsList>

                  <TabsContent value="membri" className="p-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Membro</TableHead>
                          <TableHead>Ruolo</TableHead>
                          <TableHead>Iscritto il</TableHead>
                          {canManagePartito && <TableHead>Azioni</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {partitoDetail.membri.map((m) => (
                          <TableRow key={m.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-400" />
                                <span className="font-medium">@{m.user_username}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={m.ruolo_nome === 'Presidente' ? 'default' : 'outline'}>
                                {m.ruolo_nome === 'Presidente' && <Crown className="w-3 h-3 mr-1" />}
                                {m.ruolo_nome}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {new Date(m.joined_at).toLocaleDateString('it-IT')}
                            </TableCell>
                            {canManagePartito && (
                              <TableCell>
                                <Select onValueChange={(v) => handleCambiaRuoloMembro(m.user_id, v)}>
                                  <SelectTrigger className="w-32">
                                    <SelectValue placeholder="Cambia" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {partitoDetail.partito.ruoli?.filter(r => r.nome !== 'Presidente').map((r) => (
                                      <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TabsContent>

                  <TabsContent value="annunci" className="p-4 space-y-4">
                    {canManagePartito && (
                      <div className="flex justify-end">
                        <Dialog open={annuncioDialogOpen} onOpenChange={setAnnuncioDialogOpen}>
                          <DialogTrigger asChild>
                            <Button size="sm">
                              <Megaphone className="w-4 h-4 mr-2" />
                              Nuovo Annuncio
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Pubblica Annuncio</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              <div className="space-y-2">
                                <Label>Titolo *</Label>
                                <Input
                                  value={annuncioForm.titolo}
                                  onChange={(e) => setAnnuncioForm({...annuncioForm, titolo: e.target.value})}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Contenuto *</Label>
                                <Textarea
                                  value={annuncioForm.contenuto}
                                  onChange={(e) => setAnnuncioForm({...annuncioForm, contenuto: e.target.value})}
                                  rows={4}
                                />
                              </div>
                              <div className="flex justify-end gap-3">
                                <Button variant="outline" onClick={() => setAnnuncioDialogOpen(false)}>Annulla</Button>
                                <Button onClick={handleCreaAnnuncio}>Pubblica</Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    )}
                    {partitoDetail.annunci.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Nessun annuncio</p>
                    ) : (
                      partitoDetail.annunci.map((a) => (
                        <Card key={a.id}>
                          <CardContent className="p-4">
                            <h3 className="font-semibold">{a.titolo}</h3>
                            <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{a.contenuto}</p>
                            <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
                              <User className="w-3 h-3" />
                              @{a.autore_username}
                              <Calendar className="w-3 h-3 ml-2" />
                              {new Date(a.created_at).toLocaleDateString('it-IT')}
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </TabsContent>

                  {canManagePartito && (
                    <TabsContent value="gestione" className="p-4 space-y-6">
                      {/* Assegna Presidente (Solo Admin) */}
                      {isAdmin && (
                        <div>
                          <h3 className="font-semibold mb-3 flex items-center gap-2">
                            <Crown className="w-4 h-4 text-amber-500" />
                            Assegna Presidente
                          </h3>
                          <div className="flex gap-2">
                            <Select value={selectedPresidente} onValueChange={setSelectedPresidente}>
                              <SelectTrigger className="w-64">
                                <SelectValue placeholder="Seleziona utente..." />
                              </SelectTrigger>
                              <SelectContent>
                                {allUsers.map((u) => (
                                  <SelectItem key={u.id} value={u.id}>@{u.username}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button onClick={handleAssegnaPresidente}>Assegna</Button>
                          </div>
                        </div>
                      )}

                      {/* Crea Ruolo */}
                      <div>
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          Ruoli del Partito
                        </h3>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {partitoDetail.partito.ruoli?.map((r) => (
                            <Badge key={r.id} variant="outline">
                              {r.nome} (Lv.{r.livello})
                            </Badge>
                          ))}
                        </div>
                        <Dialog open={ruoloDialogOpen} onOpenChange={setRuoloDialogOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Plus className="w-4 h-4 mr-2" />
                              Nuovo Ruolo
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Crea Ruolo</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              <div className="space-y-2">
                                <Label>Nome Ruolo *</Label>
                                <Input
                                  value={ruoloForm.nome}
                                  onChange={(e) => setRuoloForm({...ruoloForm, nome: e.target.value})}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Livello (1-8)</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  max="8"
                                  value={ruoloForm.livello}
                                  onChange={(e) => setRuoloForm({...ruoloForm, livello: parseInt(e.target.value)})}
                                />
                              </div>
                              <div className="flex justify-end gap-3">
                                <Button variant="outline" onClick={() => setRuoloDialogOpen(false)}>Annulla</Button>
                                <Button onClick={handleCreaRuolo}>Crea</Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </TabsContent>
                  )}
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Partiti;
